<template>
  <Toast />
  <div class="summary flex justify-content-between gap-3 mb-5">
    <div class="flex flex-column align-items-start gap-3 m-3">
      <h1>{{ selectedCompany?.name }}</h1>
      <!-- <p>{{ deliveryNotes.length }} documentos de transporte sin facturar</p> -->
    </div>
    <div class="flex flex-column align-items-start gap-3 m-3" v-if="availableCompanies.length > 1">
      <Select :model-value="selectedCompany" :options="availableCompanies" optionLabel="name"
        :placeholder="$t('home.selectCompany')" @change="onSelectCompany($event)" />
    </div>
  </div>
  <div class="filters">
    <Fieldset :legend="filtersLegend" :toggleable="true" class="mt-3" :collapsed="true">
      <div class="pt-3 pb-3 grid">
        <div class="col-6 md:col-6 lg:col-3">
          <Select v-model="monthSelected" :options="monthOptions" optionLabel="label"
            :placeholder="$t('home.selectMonth')" class="w-full md:w-14rem" @change="onFilterChange()" />
        </div>
      </div>
      <div class="pb-3 grid row-gap-3">
        <div class="col-6 md:col-6 lg:col-3">
          <FloatLabel>
            <MultiSelect id="vehicles-filter" filter v-model="vehiclesFilter" :options="vehicles" optionLabel="plate"
              :placeholder="$t('deliveryNotes.vehicles')"
              :selectedItemsLabel="$t('deliveryNotes.selectedVehiclesLabel', { vehiclesLength: vehiclesFilter?.length })"
              :maxSelectedLabels="2" class="w-full" @change="onFilterChange()" />
            <label for="vehicles-filter">{{ $t('deliveryNotes.vehicles') }}</label>
          </FloatLabel>
        </div>
        <div class="col-6 md:col-6 lg:col-3">
          <FloatLabel>
            <PersonFilterMultiSelect id="employees-filter" v-model="employeeFilter" :people="employees"
              :placeholder="$t('deliveryNotes.employee')"
              :selectedItemsLabel="$t('deliveryNotes.selectedEmployeesLabel', { employeesLength: employeeFilter?.length })"
              :maxSelectedLabels="2" class="w-full" @change="onFilterChange()" />
            <label for="employees-filter">{{ $t('deliveryNotes.employee') }}</label>
          </FloatLabel>
        </div>
      </div>
    </Fieldset>
  </div>
  <div class="dashboard-1-line grid mt-3 mb-6">
    <div class="col-12 md:col-10 delivery-note-per-day-chart">
      <Card>
        <template #content>
          <div class="c-chart__container">
            <canvas id="delivery-notes-chart"></canvas>
          </div>
        </template>
      </Card>
    </div>
    <div class="col-12 md:col-2 delivery-note-counter-div h-100">
      <Card class="delivery-note-counter-card">
        <template #content>
          <div class="delivery-note-counter-title">
            <p>{{ $t('home.deliveryNotesCount') }}</p>
          </div>
          <div class="delivery-note-counter-chart">
            <p>{{ deliveryNotesCounter }}</p>
          </div>
        </template>
      </Card>
    </div>
  </div>
  <div v-if="!loading && isDeliveryNotesEmpty" class="flex justify-content-center mt-5">
    <EmptyState :heading="$t('home.emptyDeliveryNotesTitle')" :text="$t('home.emptyDeliveryNotesDescription')">
    </EmptyState>
  </div>
  <div class="flex justify-content-center data-container" v-if="!loading && !isDeliveryNotesEmpty">
    <DataTable :value="deliveryNotes?.content" paginator :rows="deliveryNotes?.pageable.pageSize"
      :totalRecords="deliveryNotes?.totalElements" :loading="loading" :rowsPerPageOptions="[10, 50, 100]" :lazy="true"
      :first="(deliveryNotes?.pageable?.pageNumber ?? 0) * (deliveryNotes?.pageable?.pageSize ?? 0)" @page="onPage">
      <Column field="number" :header="$t('deliveryNotes.number')" :showFilterOperator="false"
        :showFilterMatchModes="false" :showClearButton="true" :show-apply-button="false">
        <template #filter="{ filterModel, filterCallback }">
          <InputText v-model.trim="filterModel.value" type="text" class="p-column-filter"
            :placeholder="$t('common.searchByName', { name: $t('deliveryNotes.number') })" @input="filterCallback" />
        </template>
      </Column>
      <Column field="date" :header="$t('deliveryNotes.date')" :showFilterOperator="false" :showFilterMatchModes="false"
        :showClearButton="true" :show-apply-button="false">
        <template #body="slotProps">{{
          dateHelper.formatDate(dateHelper.utcToUserTimezone(slotProps.data.date))
          }}</template>
        <template #filter="{ filterModel, filterCallback }">
          <DatePicker v-model="filterModel.value" selectionMode="range" dateFormat="dd/mm/yy"
            :placeholder="$t('common.dateFormatPlaceholder')" mask="99/99/9999" @date-select="filterCallback"
            @blur="filterCallback" />
        </template>
      </Column>
      <Column field="customer.name" :header="$t('deliveryNotes.customer')" :showFilterOperator="false"
        :showFilterMatchModes="false" :showClearButton="true" :show-apply-button="false">
        <template #filter="{ filterModel, filterCallback }">
          <InputText v-model.trim="filterModel.value" type="text" class="p-column-filter"
            :placeholder="$t('common.searchByName', { name: $t('deliveryNotes.customer') })" @input="filterCallback" />
        </template>
      </Column>
      <Column field="employee" :header="$t('deliveryNotes.employee')" :showFilterOperator="false"
        :showFilterMatchModes="false" :showClearButton="true" :show-apply-button="false">
        <template #filter="{ filterModel, filterCallback }">
          <PersonFilterMultiSelect v-model="filterModel.value" @change="filterCallback()" :people="employees"
            option-value="id"
            :placeholder="$t('common.searchByName', { name: $t('deliveryNotes.employee') })" class="p-column-filter"
            style="min-width: 14rem" :maxSelectedLabels="1" />
        </template>
        <template #body="slotProps">{{
          slotProps.data.employee
            ? slotProps.data.employee.name + ' ' + slotProps.data.employee.surname
            : slotProps.data.employeeName
        }}</template>
      </Column>
      <Column field="vehicles" :header="$t('deliveryNotes.vehicles')" :showFilterOperator="false"
        :showFilterMatchModes="false" :showClearButton="true" :show-apply-button="false">
        <template #body="slotProps">{{
          slotProps.data.vehicles
            ? (slotProps.data.vehicles as Vehicle[]).map((v) => v.plate).join(', ')
            : slotProps.data.vehiclePlates?.join(', ')
        }}</template>
        <template #filter="{ filterModel, filterCallback }">
          <MultiSelect v-model="filterModel.value" @change="filterCallback()" :options="vehicles" optionLabel="plate"
            optionValue="id" :placeholder="$t('common.searchByName', { name: $t('deliveryNotes.vehicles') })"
            class="p-column-filter" style="min-width: 14rem" :maxSelectedLabels="1">
            <template #option="slotProps">
              <div class="flex align-items-center gap-2">
                <span>{{ slotProps.option.plate }}</span>
              </div>
            </template>
          </MultiSelect>
        </template>
      </Column>
      <Column field="origin.name" :header="$t('deliveryNotes.origin')" :showFilterOperator="false"
        :showFilterMatchModes="false" :showClearButton="true" :show-apply-button="false">
        <template #body="slotProps">
          {{ getDeliveryNoteListOriginLabel(slotProps.data) }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText v-model.trim="filterModel.value" type="text" class="p-column-filter"
            :placeholder="$t('common.searchByName', { name: $t('deliveryNotes.origin') })" @input="filterCallback" />
        </template>
      </Column>
      <Column field="destination.name" :header="$t('deliveryNotes.destination')" :showFilterOperator="false"
        :showFilterMatchModes="false" :showClearButton="true" :show-apply-button="false">
        <template #body="slotProps">
          {{ getDeliveryNoteListDestinationLabel(slotProps.data) }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText v-model.trim="filterModel.value" type="text" class="p-column-filter"
            :placeholder="$t('common.searchByName', { name: $t('deliveryNotes.destination') })"
            @input="filterCallback" />
        </template>
      </Column>
      <Column field="payloads" :header="$t('deliveryNotes.payloads')" :showFilterOperator="false"
        :showFilterMatchModes="false" :showClearButton="true" :show-apply-button="false">
        <template #body="slotProps">
          <span v-html="formatPayloads(getDeliveryNoteListPayloads(slotProps.data))"></span>
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText v-model.trim="filterModel.value" type="text" class="p-column-filter"
            :placeholder="$t('common.searchByName', { name: $t('deliveryNotes.payloads') })" @input="filterCallback" />
        </template>
      </Column>
      <!-- <Column field="signed" :header="$t('deliveryNotes.signed')" dataType="boolean">
        <template #body="slotProps">{{ getSignedLabel(slotProps.data) }}</template>
      </Column>
      <Column field="billed" :header="$t('deliveryNotes.billed')" dataType="boolean">
        <template #body="slotProps">{{
          slotProps.data.billed === true ? $t('common.yes') : $t('common.no')
        }}</template>
      </Column> -->
      <Column>
        <template #body="slotProps">
          <Button icon="pi pi-ellipsis-v" text rounded @click="toggleRowMenu($event, slotProps.data)"
            :ref="el => setRowMenuRef(el, slotProps.data.id)" />
          <Menu :ref="el => setRowMenuRef(el, slotProps.data.id)" :model="getRowMenuItems(slotProps.data)"
            :popup="true" />
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script setup lang="ts">
import PersonFilterMultiSelect from '@/components/common/person/PersonFilterMultiSelect.vue'
import EmptyState from '@/components/EmptyState.vue'
import type Customer from '@/model/Customer'
import {
  getDeliveryNoteListDestinationLabel,
  getDeliveryNoteListOriginLabel,
  getDeliveryNoteListPayloads,
  type DeliveryNote,
  type Payload,
} from '@/model/DeliveryNote'
import type Employee from '@/model/Employee'
import type Page from '@/model/Page'
import type Place from '@/model/Place'
import { ChartType, type Serie, type Statistics } from '@/model/Statistics'
import type Vehicle from '@/model/Vehicle'
import router from '@/router'
import { apiService } from '@/services/ApiService'
import { customerService } from '@/services/CustomerService'
import { deliveryNoteService } from '@/services/DeliveryNoteService'
import { useDeliveryNoteDownload } from '@/composables/useDeliveryNoteDownload'
import { useFiltersLegend } from '@/composables/useFiltersLegend'
import { employeeService } from '@/services/EmployeeService'
import { placeService } from '@/services/PlaceService'
import { vehicleService } from '@/services/VehicleService'
import { ServiceError } from '@/services/util/ServiceError'
import { useProfileStore } from '@/store/profile.module'
import dateHelper from '@/utils/dateHelper'
import { countActive, isNonEmptyArray } from '@/utils/filterUtils'
import Chart from 'chart.js/auto'
import 'chartjs-adapter-moment'
import moment from 'moment'
import type { DataTablePageEvent } from 'primevue/datatable'
import type { MenuItem } from 'primevue/menuitem'
import { useToast } from 'primevue/usetoast'
import { computed, onMounted, ref, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'

Chart.register()
const storeProfile = useProfileStore()

const toast = useToast()
const { downloadDeliveryNote, getDownloadLabel } = useDeliveryNoteDownload()
const { t } = useI18n()

const selectedCompany = ref(storeProfile.selectedCompany)
const availableCompanies = storeProfile.getAvailableCompanies

const currentPage = ref(0)
const loading = ref(true)
const rowMenus = ref<Map<string, any>>(new Map())

const deliveryNotes = ref<Page<DeliveryNote>>()
const customers = ref<Customer[]>([])
const employees = ref<Employee[]>([])
const vehicles = ref<Vehicle[]>([])
const places = ref<Place[]>([])
const statistics = ref<Statistics>({} as Statistics)

const deliveryNotesCounter = ref()

const isDeliveryNotesEmpty = computed(
  () => !deliveryNotes.value || (deliveryNotes.value && deliveryNotes.value.content.length === 0)
)

const vehiclesFilter = ref<Vehicle[]>()
const employeeFilter = ref<Employee[]>()

const activeFiltersCount = computed(() =>
  countActive(isNonEmptyArray(vehiclesFilter.value), isNonEmptyArray(employeeFilter.value))
)
const filtersLegend = useFiltersLegend(activeFiltersCount)

const getMonths = () => {
  const months: any[] = []
  const now = new Date()

  for (let i = 0; i < 24; i++) {
    const startOfMonth = new Date(now.getFullYear(), now.getMonth() - i, 1) // Primer día del mes
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() - i + 1, 0, 23, 59, 59, 999) // Último día del mes

    const monthString = startOfMonth.toLocaleString('default', { month: 'long', year: 'numeric' })
    months.push({
      label: monthString,
      startDate: dateHelper.userTimezoneToUTC(startOfMonth.toISOString()),
      endDate: dateHelper.userTimezoneToUTC(endOfMonth.toISOString())
    })
  }

  // months.reverse()

  return months
}

const monthOptions = ref(getMonths())
const monthSelected: Ref<{ startDate: string; endDate: string; label: string }> = ref<{
  startDate: string
  endDate: string
  label: string
}>({} as { startDate: string; endDate: string; label: string })

onMounted(async () => {
  await loadDeliveryNotes()
  await loadDeliveryNoteStatistics()
  await loadCustomers()
  await loadVehicles()
  await loadEmployees()
  await loadPlaces()
  await loadFeatureFlags()

  await drawChart()
})

async function loadDeliveryNotes(pageNumber?: number, pageSize?: number) {
  loading.value = true

  if (!monthSelected.value || !monthSelected.value.startDate) {
    monthSelected.value = monthOptions.value[0]
  }

  const employeeIds = employeeFilter.value?.map((e) => e.id) || []
  const vehicleIds = vehiclesFilter.value?.map((e) => e.id) || []

  const response = await deliveryNoteService.findAllV2(
    pageNumber,
    pageSize,
    monthSelected.value.startDate,
    monthSelected.value.endDate,
    undefined,
    employeeIds,
    undefined,
    undefined,
    vehicleIds,
    undefined,
    undefined,
    undefined, // originName
    undefined, // destinationName
    undefined, // customerName
    undefined, // customerVatNumber
    undefined, // driverName
    undefined, // driverSurname
    false, // billed
    undefined, // uncompleted
    undefined, // manualValidationRequired
    'date',
    'desc'
  )
  if (!(response instanceof ServiceError)) {
    deliveryNotes.value = response || []
  }
  loading.value = false
}

const onPage = async (event: DataTablePageEvent) => {
  currentPage.value = event.page
  await loadDeliveryNotes(event.first / event.rows, event.rows)
}

async function loadDeliveryNoteStatistics() {
  loading.value = true

  if (!monthSelected.value || !monthSelected.value.startDate) {
    monthSelected.value = monthOptions.value[0]
  }

  const employeeIds = employeeFilter.value?.map((e) => e.id) || []
  const vehicleIds = vehiclesFilter.value?.map((e) => e.id) || []

  const response = await deliveryNoteService.findStatistics(
    [ChartType.DELIVERY_NOTES_PER_DAY, ChartType.DELIVERY_NOTES_COUNT],
    monthSelected.value.startDate,
    monthSelected.value.endDate,
    employeeIds,
    vehicleIds,
    undefined,
    undefined,
    false
  )
  if (!(response instanceof ServiceError)) {
    statistics.value = response

    deliveryNotesCounter.value = statistics.value.charts.find(
      (c) => c.type === ChartType.DELIVERY_NOTES_COUNT
    )?.series[0].data[0]
  }
  loading.value = false
}

async function loadCustomers() {
  const response = await customerService.findAll()
  if (!(response instanceof ServiceError)) {
    customers.value = response || []
  }
}

async function loadVehicles() {
  const response = await vehicleService.findAll()
  if (!(response instanceof ServiceError)) {
    vehicles.value = response || []
  }
}

async function loadEmployees() {
  const response = await employeeService.findAll()
  if (!(response instanceof ServiceError)) {
    employees.value = response || []
  }
}

async function loadPlaces() {
  const response = await placeService.findAll()
  if (!(response instanceof ServiceError)) {
    places.value = response || []
  }
}

async function loadFeatureFlags() {
  await apiService.getFeatureFlagsGlobal()

}

const getSignedLabel = (dn: DeliveryNote) => {
  return dn.mandatorySign
    ? dn.signed === true
      ? t('common.yes')
      : t('common.no')
    : t('common.notNeeded')
}

const onDeliveryNoteView = (deliveryNote: DeliveryNote) => {
  router.push(`/deliveryNotes/${deliveryNote.id}`)
}

const onDownloadPdf = async (deliveryNote: DeliveryNote) => {
  await downloadDeliveryNote(deliveryNote)
}

const setRowMenuRef = (el: any, id: string) => {
  if (el) {
    rowMenus.value.set(id, el)
  }
}

const toggleRowMenu = (event: MouseEvent, data: DeliveryNote) => {
  const menu = rowMenus.value.get(data.id)
  menu?.toggle(event)
}

const isBilled = (dn: DeliveryNote) => {
  return dn.bill !== undefined && dn.bill !== null
}

const getRowMenuItems = (data: DeliveryNote): MenuItem[] => {
  return [
    {
      label: t('common.view'),
      icon: 'pi pi-eye',
      command: () => onDeliveryNoteView(data)
    },
    {
      label: getDownloadLabel(data),
      icon: 'pi pi-file-pdf',
      command: () => onDownloadPdf(data)
    },
    {
      label: t('common.seeBill'),
      icon: 'pi pi-file',
      command: () => router.push(`/bills/${data.bill?.id}`),
      visible: data.bill !== undefined && data.bill !== null
    }
  ]
}

const onFilterChange = async () => {
  await loadDeliveryNotes()
  await loadDeliveryNoteStatistics()

  await reDrawChart()
}

let chartObject: Chart | null = null

function getData(serie: Serie): { x: number; y: number }[] {
  const data = []
  for (let i = 0; i < serie.xAxis.length; i++) {
    const label = moment(dateHelper.utcToUserTimezone(serie.xAxis[i])).valueOf()
    data.push({
      x: label,
      y: Math.trunc(serie.data[i])
    })
  }
  return data
}

const drawChart = () => {
  const container = document.getElementById('delivery-notes-chart')

  if (!container) {
    return
  }
  const canvas = container as HTMLCanvasElement
  if (!canvas) {
    return
  }

  const data = {
    // labels: statistics.value.charts[0].series[0].xAxis,
    labels: [],
    datasets: [
      {
        label: t('home.deliveryNotesChartLabel'),
        data: getData(statistics.value.charts[0].series[0]),
        backgroundColor: '#63ABFD', // no template color
        order: 1,
        showLine: true,
        tension: 0.4,
        fill: false,
        spanGaps: 1000 * 60 * 30,
        tooltip: {
          enabled: true,
          callbacks: {
            label: function (text: any) {
              return t('home.deliveryNotesChartTooltip', { count: text.parsed.y })
            }
          }
        }
      }
    ]
  }

  const chartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      tooltip: {
        callbacks: {
          title: function (tooltipItems: any[]) {
            return dateHelper.formatDate(tooltipItems[0].parsed.x)
          }
        }
      },
      legend: {
        display: true
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        suggestedMin: moment(dateHelper.utcToUserTimezone(monthSelected.value.startDate)).valueOf(),
        suggestedMax: moment(dateHelper.utcToUserTimezone(monthSelected.value.endDate)).valueOf(),
        // suggestedMin: 1725141600000,
        // suggestedMax: 1727647200000,
        type: 'time',
        time: {
          unit: 'day'
        }
      },
      y: {
        stacked: false,
        // beginAtZero: true,
        // suggestedMin: 0,
        suggestedMax: 10,
        min: 0,
        ticks: {
          precision: 0
        }
      }
    }
  }

  chartObject = new Chart(canvas, {
    type: 'bar',
    data: data,
    options: chartOptions as any
  })
}

const reDrawChart = async () => {
  if (chartObject) {
    chartObject.destroy()
  }

  drawChart()
}

const formatPayloads = (payloads: Payload[]) => {
  return (payloads as Payload[])
    .map(
      (p) => `${p.quantity?.toLocaleString('es-ES') ?? ''} ${t(`prices.priceUnit.${p.unit}`)} ${p.payload}`
    )
    .join('<br>')
}

const onSelectCompany = async (event: any) => {
  storeProfile.setSelectedCompany(event.value)

  router.go(0)
}
</script>

<style scoped>
.summary {
  padding: 15px;
  background-color: #22303c;
  color: white;
}

.info-counter {
  margin: 20px;
  padding: 30px;
  border: 3px solid #8899ac;
  border-radius: 10px;
}

.p-card {
  padding: 5px;
}

.dashboard-1-line {
  width: 100%;
  margin: 0;
}

.c-chart__container {
  height: 30vh;
  width: 100%;
}

.delivery-note-counter-div {
  margin: 0;
  padding: 10px;
}

.delivery-note-counter-card {
  height: 100%;
}

.delivery-note-counter-title {
  display: flex;
  justify-content: center;
  align-items: center;
}

.delivery-note-counter-chart {
  display: flex;
  justify-content: center;
  align-items: center;
}

.delivery-note-counter-chart p {
  font-size: 60px;
}

.delivery-note-per-day-chart {
  margin: 0;
  padding: 10px;
}

/* Estilos para el menú contextual de opciones */
:deep(.p-menu .text-red-500) {
  color: var(--red-500) !important;
}

:deep(.p-menu .p-menuitem-icon) {
  margin-right: 0.5rem;
}
</style>
